import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, InputNumber } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { selectors, updateSessionBenchmarkComposition } from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';

/**
 * Compact constant-return benchmark tile for the composition right column.
 * Always visible; the parent greys it out (via a .bmv2-off wrapper) unless the
 * benchmark type is "Constant". Saving dispatches the same
 * update_benchmark_composition endpoint as the classic editor.
 */
const ConstantBenchmarkTile: React.FC = () => {
  const dispatch = useDispatch();
  const { benchmark_constant } = useSelector(selectors.activeSession);

  const seeded = Math.round((benchmark_constant ?? 0) * 1000) / 10;
  const [value, setValue] = useState<number | null>(seeded);
  const [saving, setSaving] = useState(false);

  // Re-seed when the backend reports a new constant (e.g. after a save).
  useEffect(() => {
    setValue(Math.round((benchmark_constant ?? 0) * 1000) / 10);
  }, [benchmark_constant]);

  const dirty = value != null && Math.abs((value ?? 0) - seeded) > 1e-6;
  const canSave = value != null && value > 0;

  const onSave = () => {
    if (!canSave) return;
    setSaving(true);
    dispatch(
      updateSessionBenchmarkComposition({ constant: (value as number) / 100 }),
    );
    window.setTimeout(() => setSaving(false), 400);
  };

  return (
    <SettingsCard
      title="Constant benchmark"
      className="bmv2-card"
      footer={
        <div className="bmv2-foot">
          <div className="bmv2-foot__msg">
            {!dirty && canSave ? (
              <>
                <CheckCircleOutlined style={{ color: 'var(--s2-positive)' }} />
                Saved
              </>
            ) : (
              'Flat return target'
            )}
          </div>
          <div className="bmv2-foot__actions">
            <Button
              type="primary"
              size="small"
              loading={saving}
              disabled={!canSave || !dirty}
              onClick={onSave}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="bmv2-constant-row">
        <InputNumber
          min={0}
          max={1000}
          step={0.5}
          value={value}
          controls
          formatter={v => `${v}%`}
          parser={v => Number((v ?? '').replace('%', '')) || 0}
          onChange={v => setValue(v as number | null)}
        />
        <span className="bmv2-constant-row__hint">return over the session</span>
      </div>
    </SettingsCard>
  );
};

export default ConstantBenchmarkTile;
